import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BadgeComponent } from 'shared/components/BadgeComponent';
import { ProfileIdentitySearch } from 'features/profiles/ProfileIdentitySearch';
import { RankPrefix } from 'shared/components/RankPrefix';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import { Profile } from 'features/profiles/useProfiles';
import { findUniqueHighlightBadges, HIGHLIGHTS_SIZE } from 'features/catalog/badge-hash';
import { gradientFromHighlightBadges } from 'features/catalog/badge-hash-gradient';
import { talosBadgesLogoURL } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';
import '../gallery/BadgeGalleryView.css';
import './BadgeHashesView.css';

interface BadgeHashesViewProps {
  ownProfileId: string | null;
  onSelectProfile: (profileId: string) => void;
  onSelectBadge: (badge: Badge) => void;
  entranceDelayMs?: number;
}

type HashRow = {
  profile: Profile;
  profileId: string;
  rank: number;
  badges: Badge[];
  gradient: string;
};

export const BadgeHashesView: React.FC<BadgeHashesViewProps> = ({
  ownProfileId,
  onSelectProfile,
  onSelectBadge,
  entranceDelayMs = 0,
}) => {
  const { rankedProfiles, badgesMap } = useTalosData();
  const [searchProfileId, setSearchProfileId] = useState<string | null>(null);
  const [enterBaseMs] = useState(entranceDelayMs);
  const cardRefs = useRef<Map<string, HTMLElement | null>>(new Map());

  const rows = useMemo((): HashRow[] => {
    const result: HashRow[] = [];
    rankedProfiles.forEach((profile, index) => {
      const claimed = profile.badgesIDs.map((id) => badgesMap.get(id)).filter((b): b is Badge => !!b);
      if (claimed.length < HIGHLIGHTS_SIZE) return;
      const profileId = getFirstPartOfEmail(profile.email);
      const badges = findUniqueHighlightBadges(claimed, profileId);
      if (!badges || badges.length !== HIGHLIGHTS_SIZE) return;
      result.push({
        profile,
        profileId,
        rank: index + 1,
        badges,
        gradient: gradientFromHighlightBadges(badges),
      });
    });
    return result;
  }, [rankedProfiles, badgesMap]);

  const hashIds = useMemo(() => new Set(rows.map((row) => row.profileId)), [rows]);

  const focusOnHash = useCallback(
    (profileId: string) => {
      setSearchProfileId(profileId);
      if (!hashIds.has(profileId)) {
        onSelectProfile(profileId);
      }
    },
    [hashIds, onSelectProfile]
  );

  useEffect(() => {
    if (!searchProfileId || !hashIds.has(searchProfileId)) return;
    const timer = window.setTimeout(() => {
      cardRefs.current.get(searchProfileId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [searchProfileId, hashIds, rows]);

  return (
    <div className="bh-page">
      <header className="bh-header gallery-header">
        <ProfileIdentitySearch
          selectedProfileId={searchProfileId}
          onSelectProfile={focusOnHash}
          onClear={() => setSearchProfileId(null)}
          avatarSrc={talosBadgesLogoURL}
        >
          <h1 className="gallery-title">Badge Hashes</h1>
          <p className="gallery-subtitle">
            Unique 5-badge hashes · {rows.length} profile{rows.length === 1 ? '' : 's'}
          </p>
        </ProfileIdentitySearch>
      </header>

      {rows.length === 0 ? (
        <p className="bh-empty">No Badge Hashes yet — unique 5-badge combinations will show up here.</p>
      ) : (
        <div className="bh-grid">
          {rows.map((row, index) => {
            const isYou = ownProfileId === row.profileId;
            const isFocused = searchProfileId === row.profileId;
            const cascadeMs = index < 12 ? index * 45 : Math.min(12 * 45 + (index - 12) * 8, 700);
            const enterDelay = `${enterBaseMs + cascadeMs}ms`;

            return (
              <article
                key={row.profile.email}
                ref={(el) => {
                  if (el) cardRefs.current.set(row.profileId, el);
                  else cardRefs.current.delete(row.profileId);
                }}
                className={`bh-card${isYou ? ' bh-card--you' : ''}${isFocused ? ' bh-card--focused' : ''}`}
                style={{ ['--bh-enter-delay' as string]: enterDelay }}
              >
                <div className="bh-card-fill" aria-hidden>
                  <div className="bh-card-fill-inner" style={{ background: row.gradient }} />
                </div>
                <div className="bh-card-content">
                  <header className="bh-card-head">
                    <h2 className="bh-card-name">
                      <RankPrefix rank={row.rank} />{' '}
                      <button type="button" className="bh-name-link" onClick={() => onSelectProfile(row.profileId)}>
                        {row.profile.name}
                      </button>
                      {isYou ? ' (you)' : ''}
                    </h2>
                  </header>
                  <div className="bh-hash-row">
                    {row.badges.map((badge) => (
                      <div key={badge.id} className={`gallery-badge rarity-${badge.rarityName}`}>
                        <BadgeComponent
                          badge={badge}
                          onClick={onSelectBadge}
                          blackAndWhite={false}
                          lowOpacity={false}
                          display
                          smaller
                          showName
                          showNameHeight={false}
                          showFloatingNameShadow={false}
                          showUsefulLinksOnHover={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};
